import {
  getResumeImportCandidates,
  getResumeImportStatus,
  getResumeImportSummary,
} from "./hroneApi";
import {
  IMPORT_STATUS,
  computeImportCounts,
  isImportCandidateSaved,
  normalizeImportCandidateRow,
  normalizeImportResults,
} from "../../util/hiringUtils";
import { loadImportState } from "../../util/resumeImportStorage";

const filterSessionResults = (sessionData, status, savedTempIds = []) => {
  const { validCandidates, failedFiles } = normalizeImportResults(sessionData);
  const savedSet = new Set(savedTempIds || []);

  const pending = validCandidates.filter(
    (c) => !savedSet.has(c.tempId) && !isImportCandidateSaved(c)
  );

  if (status === IMPORT_STATUS.FAILED) {
    return { candidates: [], failedFiles, total: failedFiles.length };
  }

  const filtered = pending.filter((c) => c.importStatus === status);
  return { candidates: filtered, failedFiles: [], total: filtered.length };
};

/**
 * Job-scoped lookup — returns pending import rows straight from the DB,
 * independent of any session id or local browser cache. This is what lets
 * Needs Review / Duplicate / Failed show data that was imported in a
 * different browser/session — the backend's own status per candidate is
 * the source of truth here, not any locally-remembered temp id.
 */
const fetchFromJobCandidates = async (jobId, status, sessionId) => {
  const res = await getResumeImportCandidates(jobId, { status, session_id: sessionId || undefined });
  if (!res.ok) return null;
  const data = await res.json();
  if (!data.status) return null;

  if (status === IMPORT_STATUS.FAILED) {
    const failedRows = data.data?.failed_files || data.data?.candidates || [];
    const failedFiles = failedRows.map((item, i) => ({
      id:       item.import_candidate_id ?? item.file_id ?? `apifail-${i}`,
      fileName: item.file_name ?? item.fileName ?? `File ${i + 1}`,
      reason:   item.failure_reason ?? item.reason ?? "Could not be parsed",
    }));
    return { candidates: [], failedFiles, total: data.data?.total ?? failedFiles.length };
  }

  const rows = data.data?.candidates || [];
  const candidates = rows
    .map((item, idx) => normalizeImportCandidateRow(item, idx))
    .filter((c) => c.importStatus === status && !isImportCandidateSaved(c));

  return { candidates, failedFiles: [], total: data.data?.total ?? candidates.length };
};

const getLocalTabData = (stored, status) => {
  if (!stored) return { candidates: [], failedFiles: [], total: 0 };

  if (status === IMPORT_STATUS.FAILED) {
    const failedFiles = stored.failedFiles || [];
    return { candidates: [], failedFiles, total: failedFiles.length };
  }

  const candidates = (stored.pendingCandidates || []).filter(
    (c) => c.importStatus === status
  );
  return { candidates, failedFiles: [], total: candidates.length };
};

const fetchFromSession = async (jobId, sessionId, status, savedTempIds) => {
  const res = await getResumeImportStatus(jobId, sessionId);
  if (!res.ok) return null;
  const data = await res.json();
  if (!data.status) return null;
  return filterSessionResults(data.data, status, savedTempIds);
};

export const fetchImportSummary = async (jobId, sessionId) => {
  const stored = loadImportState(jobId);
  const effectiveSessionId = sessionId || stored?.sessionId || null;

  // Primary: job-scoped summary — DB-backed, works regardless of which
  // browser/session created the import.
  try {
    const res = await getResumeImportSummary(jobId, { session_id: effectiveSessionId || undefined });
    if (res.ok) {
      const data = await res.json();
      const counts = data?.data?.counts;
      if (data.status && counts) {
        return {
          sessionId: data.data.session_id || effectiveSessionId,
          counts: {
            NEEDS_REVIEW: counts.NEEDS_REVIEW ?? 0,
            DUPLICATE:    counts.DUPLICATE    ?? 0,
            FAILED:       counts.FAILED       ?? 0,
          },
        };
      }
    }
  } catch {
    // fall through to session status
  }

  if (effectiveSessionId) {
    try {
      const res = await getResumeImportStatus(jobId, effectiveSessionId);
      if (res.ok) {
        const data = await res.json();
        if (data.status) {
          const savedSet = new Set(stored?.savedTempIds || []);
          const { validCandidates, failedFiles } = normalizeImportResults(data.data);
          const pending = validCandidates.filter(
            (c) => !savedSet.has(c.tempId) && !isImportCandidateSaved(c)
          );
          return {
            sessionId: effectiveSessionId,
            counts: computeImportCounts(pending, failedFiles),
          };
        }
      }
    } catch {
      // fall through to localStorage
    }
  }

  if (stored) {
    return {
      sessionId: stored.sessionId,
      counts: stored.counts || computeImportCounts(stored.pendingCandidates || [], stored.failedFiles || []),
    };
  }

  return null;
};

/**
 * Loads tab data, preferring the job-scoped DB lookup so Needs Review /
 * Duplicate / Failed show candidates regardless of which browser/session
 * imported them. Falls back to the session-status API, then localStorage,
 * if the job-scoped endpoint isn't available.
 */
export const fetchImportTabData = async (jobId, status, sessionId) => {
  const stored = loadImportState(jobId);
  const effectiveSessionId = sessionId || stored?.sessionId || null;
  const savedTempIds = stored?.savedTempIds || [];

  try {
    const fromJob = await fetchFromJobCandidates(jobId, status, effectiveSessionId);
    if (fromJob) return fromJob;
  } catch {
    // fall through to session status
  }

  if (effectiveSessionId) {
    try {
      const fromSession = await fetchFromSession(jobId, effectiveSessionId, status, savedTempIds);
      if (fromSession) return fromSession;
    } catch {
      // fall through to localStorage
    }
  }

  return getLocalTabData(stored, status);
};
